import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import logo from 'assets/img/main-logo.png';
import API from 'config/ApiConfig';
import { Mosaic } from 'react-loading-indicators';


// API Base URL
const API_URL = API.API_URL;
const JWT_TOKEN = localStorage.getItem('authToken');

// Utility function to get authentication headers
const getAuthHeaders = () => ({
    'Authorization': `Bearer ${JWT_TOKEN}`,
    'Content-Type': 'application/json'
});

// Utility function to format currency in Indonesian Rupiah
const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR'
    }).format(value);
};

const Invoice = () => {
    const { siswaId, tentorId, sortMonth } = useParams();
    const [siswa, setSiswa] = useState(null);
    const [tentors, setTentors] = useState({});
    const [rekap, setRekap] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch siswa data
                const siswaResponse = await fetch(`${API_URL}/admin/siswa/${siswaId}`, {
                    headers: getAuthHeaders()
                });
                if (!siswaResponse.ok) throw new Error(`Error fetching siswa: ${siswaResponse.status}`);
                const siswaData = await siswaResponse.json();
                setSiswa(siswaData);

                // Fetch rekap data
                const rekapResponse = await fetch(`${API_URL}/admin/rekap/siswa/${siswaId}`, {
                    headers: getAuthHeaders()
                });
                if (!rekapResponse.ok) throw new Error(`Error fetching rekap: ${rekapResponse.status}`);
                const rekapData = await rekapResponse.json();

                // Fetch tentor data based on unique tentor IDs in rekapData
                const tentorIds = [...new Set(rekapData.map(item => item.tentor_id))];
                const tentorPromises = tentorIds.map(id =>
                    fetch(`${API_URL}/admin/tentor/${id}`, { headers: getAuthHeaders() })
                );
                const tentorResponses = await Promise.all(tentorPromises);
                const tentorData = await Promise.all(tentorResponses.map(res => res.json()));
                const tentorMap = tentorData.reduce((acc, tentor) => {
                    acc[tentor._id] = tentor;
                    return acc;
                }, {});

                setTentors(tentorMap);

                // Filter rekap data by month
                const filteredRekap = rekapData.filter(r => {
                    if (sortMonth === 'all' || !sortMonth) return true;
                    const month = new Date(r.waktu_pembelajaran).getMonth() + 1;
                    return month === parseInt(sortMonth, 10);
                });

                setRekap(filteredRekap);
            } catch (err) {
                setError(err.message);
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [siswaId, tentorId, sortMonth]);

    useEffect(() => {
        if (!loading) {
            window.print();
        }
    }, [loading]);

    if (loading) {
        return <div className="flex m-4 justify-center items-center w-screen"><Mosaic color="#1177bc" size="large" text="Loading.." textColor="" /></div>
      }
    if (error) return <div>Error: {error}</div>;

    // Compute total
    const totalTarif = rekap.reduce((total, r) => total + (r.harga || 0), 0);

    return (
        <div style={{ backgroundColor: '#f4f4f4', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ maxWidth: '800px', margin: '0 auto', backgroundColor: '#ffffff', padding: '20px', borderRadius: '10px', boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}>
                <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1 style={{ color: '#003b5c', fontSize: '28px', margin: '0', letterSpacing: '2px', fontWeight: 'bold' }}>NAVDAA CENDEKIA</h1>
                        <h1 style={{ color: '#003b5c', fontSize: '16px', margin: '0' }}>LES PRIVAT DAN KELAS REGULER</h1>
                    </div>
                    <div>
                        <img src={logo} alt="Company Logo" style={{ width: '180px', height: '180px' }} />
                    </div>
                </header>

                <section style={{ marginBottom: '20px', borderBottom: '2px solid #003b5c', paddingBottom: '10px' }}>
                    <p style={{ margin: '0', color: '#003b5c', fontSize: '16px', lineHeight: '1.6' }}>Laporan Pembelajaran:</p>
                    <p style={{ margin: '0', fontWeight: 'bold', color: '#003b5c', fontSize: '18px' }}>{siswa?.nama}</p>
                    <p style={{ margin: '0', color: '#003b5c', fontSize: '16px', display: 'flex' }}>
                        {siswa?.nama_sekolah}
                        <span style={{ marginLeft: '5px' }}>(Kelas {siswa?.kelas})</span>
                    </p>
                </section>

                <section style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', fontSize: '16px', color: '#003b5c' }}>
                    <div>
                        <p style={{ margin: '0' }}>Telepon  : {siswa?.telepon}</p>
                        <p style={{ margin: '0' }}>OrangTua : {siswa?.nama_orangtua}</p>
                        <p style={{ margin: '0' }}>Alamat   : {siswa?.alamat}</p>
                    </div>
                    <div>
                        <p style={{ margin: '0', textAlign: 'right' }}>Tanggal : {new Date().toLocaleDateString()}</p>
                    </div>
                </section>

                <table style={{ width: '100%', marginBottom: '20px', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#003b5c', color: '#ffffff', textAlign: 'left' }}>
                            <th style={{ padding: '10px', border: '1px solid #dddddd', textAlign: 'center' }}>No</th>
                            <th style={{ padding: '10px', border: '1px solid #dddddd', textAlign: 'center'  }}>Tanggal</th>
                            <th style={{ padding: '10px', border: '1px solid #dddddd', textAlign: 'center' }}>Materi</th>
                            <th style={{ padding: '10px', border: '1px solid #dddddd', textAlign: 'center'  }}>Durasi</th>
                            <th style={{ padding: '10px', border: '1px solid #dddddd', textAlign: 'center'  }}>Tentor</th>
                            <th style={{ padding: '10px', border: '1px solid #dddddd', textAlign: 'center'  }}>Tarif</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rekap.map((item, index) => (
                            <tr key={item._id}>
                                <td style={{ padding: '10px', border: '1px solid #dddddd', color: '#003b5c', textAlign: 'center'  }}>{index + 1}</td>
                                <td style={{ padding: '10px', border: '1px solid #dddddd', color: '#003b5c', textAlign: 'center'  }}>{new Date(item.waktu_pembelajaran).toLocaleDateString()}</td>
                                <td style={{ padding: '10px', border: '1px solid #dddddd', color: '#003b5c', fontWeight: 'bold' }}>
                                    {item.mata_pelajaran}
                                    <p style={{ fontSize: '12px' }}>Catatan :<br />{item.feedback}</p>
                                </td>
                                <td style={{ padding: '10px', border: '1px solid #dddddd', color: '#003b5c', textAlign: 'center'  }}>{item.durasi}</td>
                                <td style={{ padding: '10px', border: '1px solid #dddddd', color: '#003b5c', textAlign: 'center'  }}>
                                    {tentors[item.tentor_id]?.nama || 'Data Tentor Tidak Ditemukan'}
                                </td>
                                <td style={{ padding: '10px', border: '1px solid #dddddd', color: '#003b5c', fontSize: '14px' }}>
                                    {formatCurrency(item.harga)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <section style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                    <div style={{ width: 'auto' }}>
                        <div style={{ display: 'flex', padding: '10px', backgroundColor: '#003b5c', color: '#ffffff', borderRadius: '5px' }}>
                            <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold' }}>Total :</p>
                            <p style={{ margin: '0', paddingLeft: '5px', fontSize: '18px', fontWeight: 'bold' }}>
                                {formatCurrency(totalTarif)}
                            </p>
                        </div>
                    </div>
                </section>

                <section style={{ marginBottom: '20px' }}>
                    <h2 style={{ fontWeight: 'bold', color: '#003b5c', marginBottom: '10px' }}>Syarat dan Ketentuan</h2>
                    <p style={{ color: '#757575', fontSize: '14px', marginBottom: '5px', fontWeight: 'bold' }}>
                        Mohon untuk melakukan pembayaran ke rekening : 
                        <div><b>BNI </b></div>
                        <di><b>1792715328</b></di>
                        <div><b>An. Navalia Dewi Ernamasari</b></div>
                        Sertakan kirim bukti pembayaran/konfirmasi pembayaran.
                    </p>
                    <br />
                    <p style={{ color: '#757575', fontSize: '14px', marginBottom: '5px', fontWeight: 'bold' }}>
                        Terimakasih Atas Kepercayaan Anda
                    </p>
                    <div style={{ textAlign: 'right' }}>
                        <p style={{  margin: '0' }}>Hormat Saya,</p>
                        <p style={{ fontWeight: 'bold',margin: '0' }}>Navalia Dewi Ernamasari</p>
                    </div>
                </section>

                <footer style={{ borderTop: '2px solid #003b5c', paddingTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#003b5c', fontSize: '16px' }}>
                    <div>
                        <p style={{ margin: '0' }}><i className="fas fa-phone"></i> +6285329075397</p>
                        <p style={{ margin: '0' }}><i className="fas fa-envelope"></i> navdaacendekia@gmail.com</p>
                        <p style={{ margin: '0' }}><i className="fas fa-map-marked-alt"></i> Banjarjo RT.18, Tegaldowo, Kec. Gemolong, Kabupaten Sragen, Jawa Tengah 57274</p>
                    </div>
                    
                </footer>
            </div>

            {/* Style untuk print */}
            <style>
                {`
                    @media print {
                        body {
                            -webkit-print-color-adjust: exact !important; 
                            color-adjust: exact !important;               
                            print-color-adjust: exact !important;   
                        }
                        .print-hidden {
                            display: none;
                        }
                        .bg-blue-100 {
                            background-color: #e0f7fa !important; /* Sesuaikan warna latar belakang untuk cetak */
                        }
                        .bg-blue-800 {
                            background-color: #003b5c !important; /* Sesuaikan warna latar belakang untuk cetak */
                        }
                        .text-gray-600 {
                            color: #757575 !important; /* Sesuaikan warna teks untuk cetak */
                        }
                        .text-blue-800 {
                            color: #003b5c !important; /* Sesuaikan warna teks untuk cetak */
                        }
                        .rounded-full {
                            border-radius: 9999px !important; /* Sesuaikan border-radius untuk cetak */
                        }
                        /* Tambahkan aturan lain yang diperlukan */
                    }
                `}
            </style>
        </div>
    );
};

export default Invoice;
